import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

export const formatThaiFullDate = (datetime: string) => {
  const localTz = dayjs.tz.guess();
  const date = dayjs.utc(datetime).tz(localTz);

  return `${date.format("D MMMM")} ${date.year() + 543} เวลา ${date.format(
    "HH.mm.ss",
  )} น.`;
};

export const formatDate = (datetime: string, format = "DD/MM/YYYY") => {
  const localTz = dayjs.tz.guess();
  const date = dayjs.utc(datetime).tz(localTz);
  return date.format(format);
};

const BKK_TZ = "Asia/Bangkok";
/** How the API writes a business day that carries no time. */
const BUSINESS_DAY_FORMAT = "YYYY-MM-DD";

// True when the document's issue date, on the Bangkok calendar, falls before
// today's Bangkok date — i.e. the day has already rolled over. Reprint
// authorization is only allowed once a transaction is at least one day old.
export const isBeforeTodayBkk = (datetime: string): boolean => {
  const docDay = dayjs.utc(datetime).tz(BKK_TZ).startOf("day");
  const todayBkk = dayjs().tz(BKK_TZ).startOf("day");
  return docDay.isBefore(todayBkk);
};

/**
 * Today's Bangkok calendar day as a local date at midnight, for comparing with the business
 * days (`YYYY-MM-DD`) the API keeps: a viewer whose clock is still on yesterday elsewhere
 * must not be offered a day the server already treats as past.
 */
export const todayInBangkok = (): Dayjs =>
  dayjs(dayjs().tz(BKK_TZ).format(BUSINESS_DAY_FORMAT));

/**
 * Render a full instant as Bangkok wall-clock `DD/MM/YYYY HH:mm`.
 *
 * Used for the trip-level เวลาออก / เวลาเข้า readings, which the backend sends as
 * ISO 8601 with an explicit `+07:00` offset. A van that gets back at 00:30 belongs
 * to the NEXT calendar day, and the legacy time-only fields cannot say which day
 * that was — showing the date is the whole point.
 *
 * Deliberately NOT `formatDate()`: that one converts to `dayjs.tz.guess()`, so a
 * viewer outside Bangkok would see the branch's times shifted by their own offset.
 * These are Thai branch operating times and are only ever meaningful in Bangkok.
 *
 * Returns `""` for empty or unparseable input so callers can fall back.
 */
export const formatDateTimeBkk = (
  datetime: string | null | undefined,
  format = "DD/MM/YYYY HH:mm",
): string => {
  if (!datetime) return "";
  const d = dayjs(datetime).tz(BKK_TZ);
  return d.isValid() ? d.format(format) : "";
};

const TH_WEEKDAY_SHORT = ["อา.", "จ.", "อ.", "พ.", "พฤ.", "ศ.", "ส."];
const TH_MONTH_SHORT = [
  "ม.ค.",
  "ก.พ.",
  "มี.ค.",
  "เม.ย.",
  "พ.ค.",
  "มิ.ย.",
  "ก.ค.",
  "ส.ค.",
  "ก.ย.",
  "ต.ค.",
  "พ.ย.",
  "ธ.ค.",
];

/** Two-digit Buddhist-era year, the platform's short-date convention (2026 → 69). */
const shortBuddhistYear = (year: number): string =>
  String((year + 543) % 100).padStart(2, "0");

/**
 * Render an instant as a short Thai Buddhist-era Bangkok date-time, e.g.
 * `อ. 22 ก.ย. 69 03:00`.
 *
 * Pinned to Bangkok like {@link formatDateTimeBkk}: the auto-send schedule is a Bangkok
 * wall-clock rule, so a viewer elsewhere must still see the hour the round actually runs.
 * The Thai names are spelled out here rather than read from the dayjs locale so the output
 * never depends on which locale some other module last activated.
 *
 * Returns `""` for empty or unparseable input so callers can fall back.
 */
export const formatThaiShortDateTimeBkk = (
  datetime: string | null | undefined,
  { weekday = true, time = true }: { weekday?: boolean; time?: boolean } = {},
): string => {
  if (!datetime) return "";
  const d = dayjs(datetime).tz(BKK_TZ);
  if (!d.isValid()) return "";
  const parts = [
    `${d.date()} ${TH_MONTH_SHORT[d.month()]} ${shortBuddhistYear(d.year())}`,
  ];
  if (weekday) parts.unshift(TH_WEEKDAY_SHORT[d.day()]);
  if (time) parts.push(d.format("HH:mm"));
  return parts.join(" ");
};

/**
 * Short Thai Buddhist-era rendering of a calendar date that has no time or zone
 * (`YYYY-MM-DD`), e.g. `อ. 22 ก.ย. 69`. Read as-is — converting a bare date through a
 * time zone could move it onto the neighbouring day.
 */
export const formatThaiShortDate = (
  date: string | null | undefined,
  { weekday = true }: { weekday?: boolean } = {},
): string => {
  if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return "";
  const d = dayjs(date);
  if (!d.isValid()) return "";
  const text = `${d.date()} ${TH_MONTH_SHORT[d.month()]} ${shortBuddhistYear(d.year())}`;
  return weekday ? `${TH_WEEKDAY_SHORT[d.day()]} ${text}` : text;
};

/**
 * A calendar date (`YYYY-MM-DD`, no time or zone) the way Express prints it: `DD/MM/YY` in the
 * Buddhist era, e.g. `2026-10-05` → `05/10/69`. Returns `""` for empty or malformed input.
 */
export const formatBuddhistShortDate = (date: string | null | undefined): string => {
  if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return "";
  const d = dayjs(date);
  return d.isValid() ? `${d.format("DD/MM")}/${shortBuddhistYear(d.year())}` : "";
};

/**
 * A tax month (`YYYY-MM`, Christian era) the way Express prints ยื่นงวด: `MM/YY` in the
 * Buddhist era, e.g. `2026-10` → `10/69`. Returns `""` for empty or malformed input.
 */
export const formatBuddhistMonth = (month: string | null | undefined): string => {
  if (!month || !/^\d{4}-\d{2}$/.test(month)) return "";
  const d = dayjs(`${month}-01`);
  return d.isValid() ? `${d.format("MM")}/${shortBuddhistYear(d.year())}` : "";
};

const toSeconds = (t: string) => {
  const [hh, mm, ss] = t.split(".").map((n) => parseInt(n, 10));
  return hh * 3600 + mm * 60 + ss;
};

export const formatDuration = (timeIn: string, timeOut: string) => {
  const diff = Math.max(0, toSeconds(timeOut) - toSeconds(timeIn));
  const h = Math.floor(diff / 3600);
  const m = Math.floor((diff % 3600) / 60);
  const parts = [];
  if (h > 0) parts.push(`${h}h`);
  parts.push(`${m}min`);
  return parts.join(" ");
};
